import type { AuthTokens } from '@/entities/auth';

type Listener = (tokens: AuthTokens | null) => void;

const STORAGE_KEY = 'auth';

const listeners = new Set<Listener>();

const safeParse = (value: string | null): AuthTokens | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as AuthTokens;
  } catch {
    return null;
  }
};

const readInitialTokens = (): AuthTokens | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return safeParse(window.localStorage.getItem(STORAGE_KEY));
};

let currentTokens: AuthTokens | null = readInitialTokens();

const persist = (tokens: AuthTokens | null) => {
  if (typeof window === 'undefined') return;

  if (!tokens) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  }
};

const notify = () => {
  listeners.forEach((listener) => listener(currentTokens));
};

export const tokenManager = {
  getTokens: () => {
    // Keep in sync with storage changes that might happen outside tokenManager
    if (typeof window !== 'undefined') {
      const stored = safeParse(window.localStorage.getItem(STORAGE_KEY));
      if (
        stored &&
        (!currentTokens ||
          stored.access !== currentTokens.access ||
          stored.refresh !== currentTokens.refresh)
      ) {
        currentTokens = stored;
      } else if (!stored && currentTokens) {
        currentTokens = null;
      }
    }

    return currentTokens;
  },
  setTokens: (tokens: AuthTokens | null) => {
    currentTokens = tokens;
    persist(tokens);
    notify();
  },
  clearTokens: () => {
    currentTokens = null;
    persist(null);
    notify();
  },
  subscribe: (listener: Listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
